import { Suspense, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import { CssBaseline, GlobalStyles, ThemeProvider } from "@mui/material";

import { AppContext, MetaMaskProvider, ProfileDataCacheProvider } from "./contexts";

import { AppClient } from "./clients";

import { Route as AppRoute } from "./types";
import { getAppTheme } from "./styles/theme";
import { DARK_MODE_THEME, LIGHT_MODE_THEME } from "./utils/constants";

import { AuthorizationProvider } from "./contexts/useAuthorization";
import { NotificationsProvider } from "./components/Notifications";

import ScrollTop from "./components/ScrollTop";
import { Layout } from "./components/Layout";
import NetworkGuardBanner from "./components/NetworkGuardBanner";
import RelayStatusOverlay from "./components/RelayStatusOverlay";
import { AppRoutes, AppRoutesList } from "./config";
import ApiRoutes from "./config/api.routes";
import { toReadable } from "./utils/methods";
import { formatValue } from "./utils/formatValue";
import { useAxios } from "./services/http.service";
import { IAppContext } from "./contexts/AppContext";

function App() {
  const axios = useAxios();
  const [mode, setMode] = useState<typeof LIGHT_MODE_THEME | typeof DARK_MODE_THEME>(LIGHT_MODE_THEME);
  const [client, setClient] = useState<IAppContext | null>(null);
  const [rate, setRate] = useState<number>(0);

  useEffect(() => {
    if (!AppRoutesList) {
      console.log(AppRoutesList);
    }
    fetchRate();
  }, []);

  const themeMode = useMemo(
    () => ({
      toggleThemeMode: () => {
        setMode((prevMode) => {
          setClient(
            new AppClient(
              themeMode.toggleThemeMode,
              prevMode === LIGHT_MODE_THEME ? DARK_MODE_THEME : LIGHT_MODE_THEME,
              rate
            )
          );
          return prevMode === LIGHT_MODE_THEME ? DARK_MODE_THEME : LIGHT_MODE_THEME;
        });
      },
    }),
    []
  );

  const fetchRate = async () => {
    try {
      const result = await axios.get(ApiRoutes.ecoBank.indicators.root).then((res) => res.data);
      const trees = result.tokens.reduce((acc: any, cur: any) => acc + toReadable(cur, cur !== "0", false), 0);
      const prices = result.prices.reduce((acc: any, cur: any) => acc + toReadable(cur, true, false), 0);
      const rate = formatValue(prices / trees || 0, false);
      setRate(rate);
      setClient(new AppClient(themeMode.toggleThemeMode, mode, rate));
    } catch (e: any) {
      console.log("Failed fetch rate", { error: e });
      setClient(new AppClient(themeMode.toggleThemeMode, mode, 0));
    }
  };

  const theme = useMemo(() => getAppTheme(mode), [mode]);
  const addRoute = (route: AppRoute) => {
    if (route.isEnabled) {
      if (route?.subRoutes && Object.keys(route.subRoutes).length) {
        const { subRoutes } = route;
        const res = [<Route key={route.key} path={route.path} element={<route.component />} />];
        Object.keys(subRoutes).forEach((key) => {
          const subRoute = subRoutes[key];
          res.push(<Route key={subRoute.key} path={subRoute.path} element={<subRoute.component />} />);
        });
        return res;
      } else {
        return <Route key={route.key} path={route.path} element={<route.component />} />;
      }
    } else {
      // `null` rather than a fragment: a bare `<></>` inside this .map() has no
      // key, which React flags as a missing-key warning on every render.
      return null;
    }
  };

  const ApplicationRoutes = AppRoutesList ? AppRoutesList.map((route: AppRoute) => addRoute(route)).flat() : [];

  return (
    <MetaMaskProvider>
      <AppContext.Provider value={client || new AppClient(themeMode.toggleThemeMode, mode, 0)}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles
            styles={{
              // Clip on <body> only: with <html> left visible, body's overflow is what
              // controls page scrolling, and MUI's modal scroll lock (body { overflow:
              // hidden }) relies on that. Clipping <html> too disabled the lock, so every
              // Popover/Dialog/Menu let the page scroll away behind it.
              html: { overscrollBehaviorX: "none" },
              body: { overflowX: "clip" },
              // transitions.dev "Spinning counter" tunables, read by components/ReelNumber.
              // --reel-cell is em-based so a reel matches the line box of wherever it's dropped.
              ":root": {
                "--reel-dur": "1400ms",
                "--reel-cell": "1.3em",
                "--reel-spin-blur": "3px",
                "--reel-stagger": "90ms",
                "--reel-ease": "cubic-bezier(0.16, 1, 0.3, 1)",
                // transitions.dev "Card hover tilt" tunables, read by the LandPage hero
                // card (HeroTiltCard + hooks/useCardTilt). Peak angle is a JS constant there.
                "--tilt-perspective": "1000px",
                "--tilt-return": "1000ms",
                "--tilt-return-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                "--tilt-follow": "400ms",
                "--tilt-follow-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                "--tilt-glare-opacity": "0.32",
                "--tilt-glare-fade": "300ms",
                "--tilt-glare-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                // transitions.dev "Tabs sliding" tunables, read by the Settlement SegmentedChoice
                // pill (components/Settlement/settlementStyles). Colours stay literal in that file.
                "--tabs-dur": "250ms",
                "--tabs-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                // transitions.dev "Accordion expand" tunables, read by the LandPage role banner
                // (pages/LandPage/RoleBanner).
                "--acc-expand": "250ms",
                "--acc-collapse": "250ms",
                "--acc-chevron": "250ms",
                "--acc-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                // transitions.dev "Texts reveal" tunables, read by the LandPage hero card copy
                // (heroTextRise / heroTextLineSx).
                "--stagger-dur": "500ms",
                "--stagger-distance": "12px",
                "--stagger-stagger": "40ms",
                "--stagger-blur": "3px",
                "--stagger-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
                // transitions.dev "Thinking states" tunables, read by components/StatusCycle (the
                // LandPage and EcoBank hero cards' location line). Slower than the recipe (2s hold,
                // 150ms swap) so each state can be read and the swap reads as a calm hand-off; the
                // shimmer matches the hold so every state gets one pass. Colours come from the caller.
                // The swap was 250ms (gap 80ms), but on iPhone the EcoBank hero's swap read as a hard
                // cut at that length (the old line vanished at once) while LandPage's was fine; the
                // cause was never pinned down, and at 600ms it reads correctly there.
                "--think-hold": "4000ms",
                "--think-swap": "600ms",
                "--think-gap": "150ms",
                "--think-distance": "8px",
                "--think-blur": "2px",
                "--think-shimmer": "4000ms",
                "--think-ease": "ease-in-out",
                // transitions.dev "Icon swap" and "Page side-by-side" tunables, read by the
                // Account page invite card (pages/Profile/verification/InviteCard). Its folder
                // tabs reuse --tabs-*. The recipes' blur lanes are dropped there.
                "--icon-swap-dur": "250ms",
                "--icon-swap-start-scale": "0.25",
                "--icon-swap-ease": "ease-in-out",
                "--page-slide-dur": "250ms",
                "--page-slide-distance": "8px",
                "--page-slide-ease": "cubic-bezier(0.22, 1, 0.36, 1)",
              },
            }}
          />

          <Router>
            <ScrollTop>
              <AuthorizationProvider>
                <NotificationsProvider>
                  <ProfileDataCacheProvider>
                    <Layout>
                      {/* App-wide guards: wrong-network banner + gasless-relay status
                          surface. Both are fixed-position and render nothing while idle. */}
                      <NetworkGuardBanner />
                      <RelayStatusOverlay />
                      {/* Most pages are lazy chunks (config/app.routes.ts); this holds the content
                          area empty while one loads. The landing routes are in the main bundle. */}
                      <Suspense fallback={null}>
                        <Routes>
                          <Route
                            path={AppRoutes.Profile.path}
                            element={<Navigate replace to={AppRoutes.Profile.subRoutes.Dashboard.path} />}
                          />
                          {ApplicationRoutes}

                          <Route path="*" element={<Navigate replace to={AppRoutes.NotFound.path} />} />
                        </Routes>
                      </Suspense>
                    </Layout>
                  </ProfileDataCacheProvider>
                </NotificationsProvider>
              </AuthorizationProvider>
            </ScrollTop>
          </Router>
        </ThemeProvider>
      </AppContext.Provider>
    </MetaMaskProvider>
  );
}

export default App;
